import { useState } from 'react';
import { pilotFileExtensions } from '@common/utils/fileExtensions';
import type { IMedication } from '../../../../interfaces/medication';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, HeaderDivider } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { TextAreaField } from '../../../../fields/TextArea';
import { ReloadEpisode } from '../../../episodes/reloadEpisode';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { WarningToAddOrganizationStamp } from '../../utils/v1/WarningToAddOrganizationStamp';

export function TovV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
    const [hasOrganizationStamp, setHasOrganizationStamp] = useState(false);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedDiagnosticReportProviderOrganizationId}
                    initialPractitionerId={props.request.selectedDiagnosticReportProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedDiagnosticReportProviderOrganizationId', organizationId);
                        await props.handleChange('selectedDiagnosticReportProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedDiagnosticReportProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                    serviceProviderServiceId={props.service.id}
                    serviceType={props.service.serviceType}
                />

                <HeaderDivider text={translateKey('procedure.procedure')} />

                <AssetDropzoneWithActions
                    acceptedFiles={pilotFileExtensions}
                    assets={props.request.assets}
                    assetsBaseUrl={FilesUrlBase.requests}
                    onChange={async (assets) => await props.handleChange('assets', assets)}
                    onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                    processActivityId={props.request.id}
                    service={props.service}
                    showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                    showUseExistingExamination={!props.request.previousRequestId}
                    updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'anamnese',
                        item: props.request.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <TextAreaField
                        fieldKey="voorgeschiedenis"
                        label="Voorgeschiedenis"
                        placeholder="Voorgeschiedenis"
                    />

                    <TextAreaField
                        fieldKey="co_morbiditeiten"
                        label="Comorbiditeiten"
                        placeholder="Comorbiditeiten"
                    />

                    <TextAreaField
                        fieldKey="verwijsreden"
                        label="Verwijsreden"
                        placeholder="Verwijsreden"
                    />

                    <TextAreaField
                        fieldKey="toelichting"
                        label="Toelichting"
                        placeholder="Toelichting"
                    />
                </FormContextProvider>

                <WarningToAddOrganizationStamp
                    careProviderOrganizationId={props.request.draftCareProviderOrganizationId}
                    careProviderPractitionerId={props.request.draftCareProviderPractitionerId}
                    employmentId={props.employmentId}
                    onSaveRequest={props.onSaveRequest}
                    setHasOrganizationStamp={async (value) => setHasOrganizationStamp(value)}
                    validateRequest={props.validateRequest}
                />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled || !hasOrganizationStamp}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await props.onSave(requestId);
        if (props.reloadProcess) {
            await props.reloadProcess(ReloadEpisode.reloadAll);
        }
        if (props.reloadPatient) {
            await props.reloadPatient();
        }
    }
}

export function tovV1RequestInitializeCustomControlData() {
    return {
        voorgeschiedenis: null,
        co_morbiditeiten: null,
        verwijsreden: null,
        toelichting: null,
    };
}
